import React, { useState, useEffect } from 'react';
import { 
  UserPlus, Mail, Trash2, Send, Settings, Loader2, 
  Shield, ChevronDown, ChevronUp, Check, X, RefreshCw 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category labels for display
const CATEGORY_LABELS = {
  banks: 'Bank Accounts',
  wills: 'Will Documents',
  mutual_funds: 'Mutual Funds',
  insurance: 'Insurance Policies',
  crypto: 'Cryptocurrency',
  property: 'Properties',
  demat: 'Demat Accounts',
  others: 'Other Documents'
};

const TrustedContacts = () => {
  const [trustedContacts, setTrustedContacts] = useState([]);
  const [vaultEntries, setVaultEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({ email: '', name: '' });
  const [expandedContact, setExpandedContact] = useState(null);
  const [savingPermissions, setSavingPermissions] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(null);

  const clientToken = localStorage.getItem('clientToken');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${clientToken}` }
  };

  // Fetch trusted contacts
  const fetchTrustedContacts = async () => {
    try {
      const response = await axios.get(`${API}/client/trusted-contacts`, axiosConfig);
      setTrustedContacts(response.data.trusted_contacts || []);
    } catch (error) {
      console.error('Failed to fetch trusted contacts:', error);
    }
  };

  // Fetch vault entries for permission toggles
  const fetchVaultEntries = async () => {
    try {
      const response = await axios.get(`${API}/client/vault/entries`, axiosConfig);
      setVaultEntries(response.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch vault entries:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchTrustedContacts(), fetchVaultEntries()]);
      setIsLoading(false);
    };
    loadData();
  }, []);

  // Add new trusted contact
  const handleAddContact = async (e) => {
    e.preventDefault();
    
    if (!newContact.email || !newContact.name) {
      toast.error('Please enter both name and email');
      return;
    }

    setIsAddingContact(true);
    try {
      const response = await axios.post(`${API}/client/trusted-contacts`, newContact, axiosConfig);
      // Backend now auto-sends the invitation email on add. If SMTP failed,
      // the response carries invitation_sent=false and a clarifying message.
      if (response.data?.invitation_sent === false) {
        toast.warning(response.data?.message || 'Contact added, but the invitation email could not be sent. Use "Resend Invitation" below.');
      } else {
        toast.success('Invitation email sent! Configure sharing permissions below.');
      }
      setNewContact({ email: '', name: '' });
      await fetchTrustedContacts();
      // Expand the newly created contact
      setExpandedContact(response.data.id);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add trusted contact';
      toast.error(message);
    } finally {
      setIsAddingContact(false);
    }
  };

  // Remove trusted contact
  const handleRemoveContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to remove this trusted contact? They will lose all access.')) {
      return;
    }

    try {
      await axios.delete(`${API}/client/trusted-contacts/${contactId}`, axiosConfig);
      toast.success('Trusted contact removed');
      await fetchTrustedContacts();
      if (expandedContact === contactId) {
        setExpandedContact(null);
      }
    } catch (error) {
      toast.error('Failed to remove trusted contact');
    }
  };

  // Update permissions for a contact (optimistic update - no re-fetch)
  const handleUpdatePermissions = async (contactId, entryIds, previousIds) => {
    setSavingPermissions(contactId);
    try {
      await axios.put(
        `${API}/client/trusted-contacts/${contactId}/permissions`,
        { entry_ids: entryIds },
        axiosConfig
      );
      toast.success('Sharing permissions updated');
      // No re-fetch needed - state already updated optimistically
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update permissions';
      toast.error(message);
      // Revert to previous state on error
      setTrustedContacts(prev => prev.map(c => 
        c.id === contactId ? { ...c, shared_entry_ids: previousIds } : c
      ));
    } finally {
      setSavingPermissions(null);
    }
  };

  // Send invitation email
  const handleSendInvitation = async (contactId) => {
    setSendingInvite(contactId);
    try {
      await axios.post(
        `${API}/client/trusted-contacts/${contactId}/send-invitation`,
        {},
        axiosConfig
      );
      toast.success('Invitation email resent!');
      await fetchTrustedContacts();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send invitation';
      toast.error(message);
    } finally {
      setSendingInvite(null);
    }
  };

  // Toggle entry permission for a contact (optimistic update)
  const toggleEntryPermission = (contact, entryId) => {
    const currentIds = contact.shared_entry_ids || [];
    const newIds = currentIds.includes(entryId)
      ? currentIds.filter(id => id !== entryId)
      : [...currentIds, entryId];
    
    // Optimistically update local state immediately (prevents re-render of all toggles)
    setTrustedContacts(prev => prev.map(c => 
      c.id === contact.id ? { ...c, shared_entry_ids: newIds } : c
    ));
    
    // Then persist to server
    handleUpdatePermissions(contact.id, newIds, currentIds);
  };

  // Group entries by category
  const groupedEntries = vaultEntries.reduce((acc, entry) => {
    const category = entry.category || 'others';
    if (!acc[category]) acc[category] = [];
    acc[category].push(entry);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6 flex justify-center items-center min-h-[200px]">
          <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="trusted-contacts-section">
      <CardHeader className="border-b border-slate-800">
        <CardTitle className="text-white flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <Shield className="w-5 h-5 text-amber-400" />
          </div>
          Trusted Contacts
        </CardTitle>
        <p className="text-slate-400 text-sm mt-1">
          Designate trusted contacts who can access your important documents in case of emergency.
        </p>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* Add New Contact Form */}
        <div className="bg-slate-800/30 rounded-xl p-4">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-amber-400" />
            Add Trusted Contact
          </h3>
          <form onSubmit={handleAddContact} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                placeholder="Contact's full name"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="trusted-contact-name-input"
              />
            </div>
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Contact's email address"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="trusted-contact-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={isAddingContact}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold whitespace-nowrap"
              data-testid="add-trusted-contact-btn"
            >
              {isAddingContact ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Contact
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Existing Trusted Contacts */}
        {trustedContacts.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No trusted contacts added yet.</p>
            <p className="text-sm mt-1">Add someone you trust to access your important documents.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {trustedContacts.map((contact) => (
              <div
                key={contact.id}
                className="border border-slate-700 rounded-xl overflow-hidden"
                data-testid={`trusted-contact-${contact.id}`}
              >
                {/* Contact Header — stacks vertically on mobile so action buttons stay reachable */}
                <div className="flex flex-col gap-3 p-4 bg-slate-800/30 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                      <span className="text-slate-900 font-bold">
                        {contact.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium truncate">{contact.name}</p>
                      <p className="text-slate-400 text-sm flex items-center gap-1 min-w-0">
                        <Mail className="w-3 h-3 shrink-0" />
                        <span className="truncate">{contact.email}</span>
                      </p>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ml-2 ${
                      contact.status === 'active' 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : contact.status === 'pending'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-slate-500/20 text-slate-400'
                    }`}>
                      {contact.status === 'active' ? 'Active' : contact.status === 'pending' ? 'Pending' : contact.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                    {contact.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => handleSendInvitation(contact.id)}
                        disabled={sendingInvite === contact.id}
                        className="bg-emerald-500 hover:bg-emerald-400 text-white flex-1 sm:flex-none"
                        data-testid={`send-invite-${contact.id}`}
                      >
                        {sendingInvite === contact.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Resend Invitation
                          </>
                        )}
                      </Button>
                    )}
                    {/* Active contacts must use the normal password reset flow,
                        not the invitation token (which would otherwise let
                        anyone with the email link reset an active account). */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setExpandedContact(expandedContact === contact.id ? null : contact.id)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-800 flex-1 sm:flex-none"
                      data-testid={`manage-sharing-${contact.id}`}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Manage Sharing
                      {expandedContact === contact.id ? (
                        <ChevronUp className="w-4 h-4 ml-1" />
                      ) : (
                        <ChevronDown className="w-4 h-4 ml-1" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRemoveContact(contact.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 shrink-0"
                      data-testid={`remove-contact-${contact.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Permissions Panel */}
                {expandedContact === contact.id && (
                  <div className="p-4 border-t border-slate-700 bg-slate-900/50">
                    <p className="text-slate-400 text-sm mb-4">
                      Toggle which entries this person can view. They will have read-only access to download shared documents.
                    </p>
                    
                    {Object.keys(groupedEntries).length === 0 ? (
                      <p className="text-slate-500 text-center py-4">
                        No vault entries to share. Add entries to your Life Vault first.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {Object.entries(groupedEntries).map(([category, entries]) => (
                          <div key={category} className="bg-slate-800/30 rounded-lg p-3">
                            <h4 className="text-amber-400 font-medium mb-3 text-sm uppercase tracking-wide">
                              {CATEGORY_LABELS[category] || category}
                            </h4>
                            <div className="space-y-2">
                              {entries.map((entry) => {
                                const isShared = contact.shared_entry_ids?.includes(entry.id);
                                
                                // Priority: save_as > specific field based on category > generic fallback
                                const getEntryDisplayName = () => {
                                  // First check if save_as exists (user-defined name)
                                  if (entry.save_as) return entry.save_as;
                                  
                                  const data = entry.data || {};
                                  
                                  // Category-specific field mapping
                                  switch (entry.category) {
                                    case 'banks':
                                      return data.institution_name || data.bank_name || 'Bank Account';
                                    case 'mutual_funds':
                                      return data.advisor_name || data.institution_name || data.fund_name || 'Mutual Fund';
                                    case 'insurance':
                                      return data.policy_provider || data.company_name || data.policy_name || 'Insurance Policy';
                                    case 'crypto':
                                      return data.exchange_name || data.wallet_name || 'Crypto Wallet';
                                    case 'property':
                                      return data.property_name || data.address || data.location || 'Property';
                                    case 'demat':
                                      return data.broker_name || data.depository || 'Demat Account';
                                    case 'wills':
                                      return data.title || data.description || 'Will Document';
                                    case 'others':
                                      return data.title || data.description || data.name || 'Other Document';
                                    default:
                                      return data.name || data.title || data.description || 'Entry';
                                  }
                                };
                                
                                const entryName = getEntryDisplayName();
                                
                                return (
                                  <div
                                    key={entry.id}
                                    className="flex items-center justify-between p-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors"
                                  >
                                    <span className="text-slate-300 text-sm truncate flex-1 mr-4">
                                      {entryName}
                                    </span>
                                    <div className="flex items-center gap-2">
                                      {savingPermissions === contact.id && (
                                        <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                                      )}
                                      <Switch
                                        checked={isShared}
                                        onCheckedChange={() => toggleEntryPermission(contact, entry.id)}
                                        disabled={savingPermissions === contact.id}
                                        className="data-[state=checked]:bg-emerald-500"
                                        data-testid={`toggle-entry-${entry.id}`}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Summary */}
                    <div className="mt-4 p-3 bg-slate-800/50 rounded-lg flex items-center justify-between">
                      <span className="text-slate-400 text-sm">
                        {contact.shared_entry_ids?.length || 0} entries shared with {contact.name}
                      </span>
                      {contact.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleSendInvitation(contact.id)}
                          disabled={sendingInvite === contact.id}
                          className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                        >
                          {sendingInvite === contact.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Resend Invitation
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrustedContacts;
